import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Lock, Sparkles } from 'lucide-react';
import React from 'react';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { SmallImageHintProps } from './types';

/**
 * Подсказка «картинка мелкая» с кнопкой «Улучшить».
 *
 * Только подсвечивает — сама ничего не грузит: модель качается, когда человек нажал
 * «Улучшить» и поле открыло модалку. Цвет — тёмный оттенок `DarkYellow`: `warning`
 * годится только для тёмного фона (`.claude/rules/design-system.md`, §1).
 *
 * Закрытое тарифом улучшение не прячется: совет загрузить крупнее полезен и без тарифа,
 * а замок после подписи кнопки — тот же, что у пункта меню с фичей.
 */
export function SmallImageHint({ kind, size, onUpscale, locked, onLockedClick, compact, isLoading, className }: SmallImageHintProps) {
  const { t } = useLingui();
  const { width, height } = size;
  // `locked` без обработчика — кнопки нет: доступ ещё проверяется, а открытая кнопка,
  // которая через миг получит замок, обманула бы.
  const handleClick = locked ? onLockedClick : onUpscale;

  const button = handleClick && (
    <UDButton
      variant="secondary"
      slim
      onClick={handleClick}
      loading={isLoading}
      // В компактном варианте строки текста нет — размер остаётся во всплывающей подсказке.
      title={compact ? t`Картинка мелкая — ${width}×${height} px` : undefined}
      className="shrink-0"
    >
      <span className="flex items-center gap-1.5">
        <Sparkles size={14} aria-hidden />
        <Trans>Улучшить</Trans>
        {locked && <Lock size={14} aria-label={t`Доступно на платном тарифе`} />}
      </span>
    </UDButton>
  );

  if (compact) return button ? <div className={className}>{button}</div> : null;

  return (
    <div className={classNames('flex flex-wrap items-center gap-3', className)}>
      <UDText variant="text-2" className="text-darkYellow-800">
        {kind === 'graphic' ? (
          <Trans>
            Логотип мелкий — {width}×{height} px, на трансляции будет размытым
          </Trans>
        ) : (
          <Trans>
            Фото мелкое — {width}×{height} px, на трансляции будет размытым
          </Trans>
        )}
      </UDText>
      {button}
    </div>
  );
}

export default SmallImageHint;
